/* eslint-disable @typescript-eslint/no-empty-function */
import {ReactElement, useContext, useEffect, useMemo, useState} from "react";

import {RenovationSetupDrawerMetadata, } from "./types";

import {Spinner} from "@zendeskgarden/react-loaders";
import {toast} from "react-toastify";
import {
    GetAnalystRenovationsQuery,
    MutationUpdateRenovationsNumbersByYearAndTypeArgs,
    useGetAnalystRenovationsLazyQuery,
    useUpdateRenovationPackageCountMutation,
    VersionType,
} from "../../../__generated__/generated_types";
import {userIsAdmin, userIsReadOnly} from "../../../authorization/AuthorizationCheckers";
import AnalystRenovationDrawer from "../../../components/analyst-renovations-drawers/AnalystRenovationDrawer";
import useGetClientCostCategories from "../../../components/analyst-renovations-drawers/data/UseGetClientCostCategories";
import {
    PropertiesAndUnitTypes,
    useGetPropertiesAndUnitTypes
} from "../../../components/analyst-renovations-drawers/data/UseGetPropertiesAndUnitTypes";
import {AnalystSections} from "../../../components/analyst/section-tabs/AnalystSectionTabs";
import FeatureRestrictedOverlay from "../../../components/feature-restricted-overlay/FeatureRestrictedOverlay";
import VZAlertNoUnitTypes from "../../../components/vz-ui/vz-alert/prebuilts/VZAlertNoUnitTypes";
import {AuthContext} from "../../../contexts/AuthContext";
import {Property, useProperties} from "../../../contexts/properties/PropertiesContext";
import {useSettings} from "../../../contexts/settings/SettingsContext";
import {getMonths} from "../../../utils/months-helper";
import {AnalystDashboardProperties} from "../AnalystDashboardProperties";
import {AnalystRenovationsTableMemo, RenoTableOnChange, UnitTypeWithRenovationPackages} from "./AnalystRenovationsTable";
import {transformRenoApiDataForTableUse} from "./utils/reno-table-utils";

type RenovationPackagesQueryData = GetAnalystRenovationsQuery["queryRenovationPackagesPaginated"]["items"];

export type RenovationData = {
    propertyData: PropertiesAndUnitTypes,
    renovationPackageData: RenovationPackagesQueryData|undefined,
    months: string[],
    year: number,
    startReforecastMonthIndex: number,
}

interface DataState {
    isDataReady: boolean;
    getRenovationData: () => RenovationData;
    fetchRenovationData: () => void;
}

const defaultRenovationData: RenovationData = {
    propertyData: [],
    renovationPackageData: undefined,
    months: [],
    year: new Date().getFullYear(),
    startReforecastMonthIndex: 0,
};

export function useAnalystRenovationsData(property: Property | undefined, budgetYear: number): DataState {
    const [isDataReady, setDataReady] = useState<boolean>(false);
    const [data, setData] = useState<RenovationPackagesQueryData | undefined>(undefined);
    const [partialData, setPartialData] = useState<RenovationPackagesQueryData>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { year, startReforecastMonthIndex, isDefaults } = useSettings();
    // TODO - migrate to common place from renovation drawer
    const {isDataReady: isPropertyAndUnitTypesDataReady, data: propertiesAndUnitTypes} = useGetPropertiesAndUnitTypes(budgetYear);
    const months = getMonths(property);
    const propertyId = property?.id;

    const [
        getRenovationPackages,
        { data: renovationPackageData, loading: renovationPackagesLoading, error: renovationPackagesError }
    ] = useGetAnalystRenovationsLazyQuery({
        // This fixes what looks like a bug in the Apollo cache system.
        // For whatever reason, with "network-only", this query can get into
        // a refresh loop without any subsequent calls to "getRenovationPackages"
        // in the useEffect.
        // Repo:
        //   1. Set this to "network-only"
        //   2. Navigate to Planning Hub > Financials
        //   3. Navigate to Planning Hub > Renovations
        //   4. Navigate to Reports > Overview
        //   5. Navigate to Planning Hub > Financials
        //   6. Navigate to Planning Hub > Renovations (watch infinite requests start happening on a schedule).
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        if(!renovationPackagesLoading && renovationPackageData && propertyId) {
            if(renovationPackageData.queryRenovationPackagesPaginated.hasMore && renovationPackageData.queryRenovationPackagesPaginated.cursor) {
                const propIds:string[] = [propertyId];
                const newPartialData = [...partialData, ...renovationPackageData.queryRenovationPackagesPaginated.items];
                setPartialData(newPartialData);
                getRenovationPackages({
                    variables: {
                        cursor: renovationPackageData.queryRenovationPackagesPaginated.cursor,
                        propertyIds: propIds,
                        yearAndTypes: [
                            {
                                year: year,
                                versionType: VersionType.Reforecast
                            },
                            {
                                year: year + 1,
                                versionType: VersionType.Budget
                            }
                        ],
                        budgetYear: budgetYear
                    }
                });
            } else {
                setData([...partialData, ...renovationPackageData.queryRenovationPackagesPaginated.items]);
                setLoading(false);
                setPartialData([]);
                setDataReady(true);
            }
        }
    }, [renovationPackagesLoading, renovationPackageData, property, budgetYear]);

    function fetchRenovationData() {
        if (!propertyId || !year) {
            return;
        }

        // We're going to assume all properties have the same budget and reforecast years.
        // That is safe assumption at this point and won't change.
        const propIds:string[] = [propertyId];
        setData(undefined);
        setDataReady(false);
        getRenovationPackages({
            variables: {
                propertyIds: propIds,
                yearAndTypes: [
                    {
                        year: year,
                        versionType: VersionType.Reforecast
                    },
                    {
                        year: year + 1,
                        versionType: VersionType.Budget
                    }
                ],
                budgetYear: budgetYear
            }
        });
    }

    useEffect(() => {
            if(isDefaults || !isPropertyAndUnitTypesDataReady || !propertiesAndUnitTypes || !propertyId){
                return;
            }

            fetchRenovationData();
        },
        [isDefaults, isPropertyAndUnitTypesDataReady, propertiesAndUnitTypes, year, propertyId]);

    useEffect(
        () => {
            if(!isPropertyAndUnitTypesDataReady || !propertiesAndUnitTypes || !data || loading){

                setDataReady(false);
                return;
            }
            // setDataReady(true);
        },
        [isPropertyAndUnitTypesDataReady, propertiesAndUnitTypes, data, loading, renovationPackagesError]
    );

    const getRenovationData = (): RenovationData => {
        return {
            propertyData: propertiesAndUnitTypes.filter(property => property.id == propertyId),
            renovationPackageData: data,
            months,
            year,
            startReforecastMonthIndex
        };
    };

    return {
        isDataReady,
        getRenovationData,
        fetchRenovationData,
    };
}

const AnalystRenovations = (props: {
    setSectionReady: (sectionId: AnalystSections, isReady: boolean) => void,
    hideSpreadsheet?: boolean,
    propertiesFilter: AnalystDashboardProperties,
    lockedProperties: {
        reforecastLocked: string[],
        budgetLocked: string[],
    },
    budgetYear: number
}): ReactElement => {

    const {
        setSectionReady,
        hideSpreadsheet,
        lockedProperties,
    } = props;

    const [showRenovationSetupDrawer, setShowRenovationSetupDrawer] = useState<boolean>(false);
    const [renovationSetupDrawerMetadata, setRenovationSetupDrawerMetadata] = useState<RenovationSetupDrawerMetadata>({propertyId: "", unitTypeId: "", unitTypeName: ""});
    const [renovationData, setRenovationData] = useState<RenovationData>(defaultRenovationData);

    const {currentProperty} = useProperties();
    const {isDataReady, getRenovationData, fetchRenovationData } = useAnalystRenovationsData(currentProperty, props.budgetYear);
    const [updateRenovationPackageCountMutation] = useUpdateRenovationPackageCountMutation();
    const { user } = useContext(AuthContext);

    const {
        data: renovationCostCategories,
    } = useGetClientCostCategories(props.budgetYear);

    async function savePackageCounts(changes: RenoTableOnChange[]) {
        if (!currentProperty) {
            return;
        }

        const groupedPayloads = new Map<string, MutationUpdateRenovationsNumbersByYearAndTypeArgs>();

        for (const {packageId, version, monthIndex, value} of changes) {
            const key = `${packageId}_${version}`;
            const year = version == VersionType.Reforecast ? props.budgetYear - 1 : props.budgetYear;

            let payload = groupedPayloads.get(key);

            if (!payload) {
                payload = {
                    renovationPackageId: packageId,
                    yearAndType: {
                        year,
                        versionType: version,
                    },
                    expected: [],
                    budgetYear: props.budgetYear
                };
                groupedPayloads.set(key, payload);
            }

            payload.expected.push({monthIndex, value});
        }

        const payloadPromises = Array.from(groupedPayloads.values()).map(payload =>
            updateRenovationPackageCountMutation({variables: {
                ...payload,
                budgetYear: props.budgetYear
            }})
        );

        const saveResults = await Promise.allSettled(payloadPromises);

        if (saveResults.every(x => x.status == "fulfilled")) {
            toast.success("Values saved");
        } else {
            toast.error("There was a problem saving, please refresh and try again");
            fetchRenovationData();
        }
    }

    useEffect(() => {
        if (isDataReady) {
            setRenovationData(getRenovationData());
            setSectionReady(AnalystSections.Renovations, true);
        } else {
            setRenovationData(defaultRenovationData);
        }
    }, [isDataReady]);

    const openPackageSetupDrawerIfNeeded = (unitTypeId: string, unitTypeName: string) => {
        if (!currentProperty || !editAllowed) {
            return;
        }

        setRenovationSetupDrawerMetadata({propertyId: currentProperty.id, unitTypeId, unitTypeName });
        setShowRenovationSetupDrawer(true);
    };

    const editAllowed = useMemo(() => {
        if (userIsReadOnly(user) || !currentProperty || (lockedProperties.reforecastLocked.includes(currentProperty.id) && lockedProperties.budgetLocked.includes(currentProperty.id))) {
            return false;
        }

        return true;
    }, [currentProperty, user, lockedProperties]);

    const builtRenovationDataForTable = useMemo<UnitTypeWithRenovationPackages[]>(() => {
        const unitTypesWithRenoData = transformRenoApiDataForTableUse(renovationData);
        return unitTypesWithRenoData;
    }, [renovationData]);

    if (!currentProperty) {
        return <Spinner />;
    }

    return (
        <>
            {!hideSpreadsheet &&
                <FeatureRestrictedOverlay />
            }
            {isDataReady && getRenovationData().propertyData[0]?.unitTypesCustom.length == 0 ?
                <VZAlertNoUnitTypes userIsAdmin={userIsAdmin(user)} />
                :
                <AnalystRenovationsTableMemo
                    property={currentProperty}
                    editAllowed={editAllowed}
                    isLoading={!isDataReady}
                    renovationData={builtRenovationDataForTable}
                    onOpenDrawer={openPackageSetupDrawerIfNeeded}
                    onChange={savePackageCounts}
                />
            }
            <AnalystRenovationDrawer
                show={showRenovationSetupDrawer}
                propertyId={renovationSetupDrawerMetadata?.propertyId ?? ""}
                unitTypeId={renovationSetupDrawerMetadata?.unitTypeId ?? ""}
                unitTypeName={renovationSetupDrawerMetadata?.unitTypeName ?? ""}
                renovationCostCategories={renovationCostCategories}
                handleClose={(triggerRefetch) => {
                    if (triggerRefetch) {
                        fetchRenovationData();
                    }
                    setShowRenovationSetupDrawer(false);
                }}
                budgetYear={props.budgetYear}
            />
        </>
    );
};

export default AnalystRenovations;
